import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import HowToVoteIcon from "@mui/icons-material/HowToVote";
import GooglePlacesAutocomplete, {
  geocodeByPlaceId,
} from "react-google-places-autocomplete";
import { US_STATES } from "../../utils/usStates";

const GOOGLE_PLACES_API_KEY =
  process.env.REACT_APP_GOOGLE_PLACES_API_KEY ||
  process.env.REACT_PUBLIC_GOOGLE_PLACES_API_KEY ||
  process.env.REACT_APP_GOOGLE_CIVIC_API_KEY ||
  "";

const PRIMARY = "#047857";
const PRIMARY_HOVER = "#065f46";

function parseAddressComponents(details) {
  if (!details?.address_components) return null;
  let streetNumber = "";
  let route = "";
  let city = "";
  let state = "";
  let zipcode = "";
  details.address_components.forEach((component) => {
    const types = component.types || [];
    if (types.includes("street_number")) streetNumber = component.long_name;
    else if (types.includes("route")) route = component.long_name;
    else if (
      (types.includes("locality") ||
        types.includes("sublocality") ||
        types.includes("neighborhood")) &&
      !city
    ) {
      city = component.long_name;
    } else if (types.includes("administrative_area_level_1")) {
      state = component.short_name;
    } else if (types.includes("postal_code")) {
      zipcode = component.long_name;
    }
  });
  return {
    street_1:
      streetNumber && route ? `${streetNumber} ${route}` : route || streetNumber,
    city,
    state,
    zipcode,
  };
}

/**
 * Address lookup form for the public /elections hub.
 *
 * Mirrors EditAddressDialog's Google Places integration so users get the same
 * one-click address experience used elsewhere in the app. Falls back to a
 * "Browse by state" select when the user wants broad results or address
 * autocomplete is unavailable.
 *
 * onSubmit is called with `{ street, city, state, zip }` (any subset) and the
 * caller is responsible for the network call. Submitting the address triggers
 * the public DW elections endpoint; the address is never stored client- or
 * server-side beyond the in-flight request.
 */
export default function PublicElectionAddressForm({
  onSubmit,
  loading = false,
  initial = null,
}) {
  const hasApiKey = !!GOOGLE_PLACES_API_KEY;
  const [mode, setMode] = useState(hasApiKey ? "address" : "state");
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [address, setAddress] = useState({
    street_1: initial?.street_1 || "",
    city: initial?.city || "",
    state: initial?.state || "",
    zipcode: initial?.zipcode || "",
  });
  const [stateOnly, setStateOnly] = useState(initial?.state || "");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!hasApiKey && mode !== "state") setMode("state");
  }, [hasApiKey, mode]);

  const handlePlaceSelection = useCallback(async (placeOption) => {
    setSelectedPlace(placeOption);
    const placeId = placeOption?.value?.place_id;
    if (!placeId) return;
    try {
      const geocodeResults = await geocodeByPlaceId(placeId);
      const parsed = parseAddressComponents(geocodeResults?.[0]);
      if (parsed) {
        setAddress((prev) => ({
          street_1: parsed.street_1 || prev.street_1,
          city: parsed.city || prev.city,
          state: parsed.state || prev.state,
          zipcode: parsed.zipcode || prev.zipcode,
        }));
        setError(null);
      }
    } catch {
      setError("Unable to load address details. Please try another address.");
    }
  }, []);

  const isAddressComplete = useMemo(
    () =>
      !!(
        address.street_1?.trim() &&
        address.city?.trim() &&
        address.state?.trim() &&
        address.zipcode?.trim()
      ),
    [address]
  );

  const handleSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (loading) return;
    if (mode === "address") {
      if (!isAddressComplete) {
        setError("Enter a complete address (street, city, state, zip).");
        return;
      }
      setError(null);
      onSubmit?.({
        street: address.street_1.trim(),
        city: address.city.trim(),
        state: address.state.trim().toUpperCase(),
        zip: address.zipcode.trim(),
      });
    } else {
      if (!stateOnly) {
        setError("Choose a state.");
        return;
      }
      setError(null);
      onSubmit?.({ state: stateOnly });
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
      sx={{
        backgroundColor: "#ffffff",
        borderRadius: 3,
        p: { xs: 2, sm: 3 },
        boxShadow: "0 10px 30px rgba(15, 23, 42, 0.12)",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        sx={{ mb: 2, alignItems: "center" }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "1rem",
            color: "#0f172a",
            flex: 1,
          }}
        >
          Find your 2026 elections
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            type="button"
            size="small"
            disableRipple
            onClick={() => setMode("address")}
            disabled={!hasApiKey}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 999,
              px: 2,
              minHeight: 36,
              backgroundColor: mode === "address" ? PRIMARY : "transparent",
              color: mode === "address" ? "#ffffff" : "#0f172a",
              border: "1px solid",
              borderColor: mode === "address" ? PRIMARY : "#cbd5e1",
              "&:hover": {
                backgroundColor:
                  mode === "address" ? PRIMARY_HOVER : "#f1f5f9",
              },
            }}
          >
            By address
          </Button>
          <Button
            type="button"
            size="small"
            disableRipple
            onClick={() => setMode("state")}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 999,
              px: 2,
              minHeight: 36,
              backgroundColor: mode === "state" ? PRIMARY : "transparent",
              color: mode === "state" ? "#ffffff" : "#0f172a",
              border: "1px solid",
              borderColor: mode === "state" ? PRIMARY : "#cbd5e1",
              "&:hover": {
                backgroundColor: mode === "state" ? PRIMARY_HOVER : "#f1f5f9",
              },
            }}
          >
            By state
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {mode === "address" && hasApiKey ? (
        <Box>
          <GooglePlacesAutocomplete
            apiKey={GOOGLE_PLACES_API_KEY}
            autocompletionRequest={{
              componentRestrictions: { country: "us" },
              types: ["address"],
            }}
            debounce={300}
            selectProps={{
              value: selectedPlace,
              onChange: handlePlaceSelection,
              placeholder: "Start typing your address…",
              isClearable: true,
              "aria-label": "Search your address",
              styles: {
                control: (provided, state) => ({
                  ...provided,
                  minHeight: 48,
                  borderRadius: 12,
                  borderColor: state.isFocused ? PRIMARY : "#cbd5e1",
                  boxShadow: state.isFocused
                    ? `0 0 0 1px ${PRIMARY}`
                    : "none",
                  "&:hover": {
                    borderColor: state.isFocused ? PRIMARY : "#94a3b8",
                  },
                }),
                placeholder: (provided) => ({
                  ...provided,
                  color: "#475569",
                }),
                singleValue: (provided) => ({
                  ...provided,
                  color: "#0f172a",
                }),
                input: (provided) => ({
                  ...provided,
                  color: "#0f172a",
                }),
                menu: (provided) => ({
                  ...provided,
                  zIndex: 1400,
                  borderRadius: 12,
                  overflow: "hidden",
                }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isFocused ? "#f1f5f9" : "#ffffff",
                  color: "#0f172a",
                  paddingTop: 10,
                  paddingBottom: 10,
                }),
              },
            }}
          />
          {(address.street_1 ||
            address.city ||
            address.state ||
            address.zipcode) && (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: 2,
                backgroundColor: "#f0fdf4",
                border: "1px solid #bbf7d0",
              }}
            >
              <Typography
                sx={{ fontWeight: 700, color: "#0f172a", fontSize: "0.85rem" }}
              >
                {address.street_1}
              </Typography>
              <Typography sx={{ color: "#0f172a", fontSize: "0.85rem" }}>
                {[address.city, address.state, address.zipcode]
                  .filter(Boolean)
                  .join(", ")}
              </Typography>
            </Box>
          )}
        </Box>
      ) : (
        <TextField
          select
          fullWidth
          label="Choose your state"
          value={stateOnly}
          onChange={(e) => setStateOnly(e.target.value)}
          variant="outlined"
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              minHeight: 56,
              "& fieldset": { borderColor: "#cbd5e1" },
              "&:hover fieldset": { borderColor: "#94a3b8" },
              "&.Mui-focused fieldset": {
                borderColor: PRIMARY,
                borderWidth: 2,
              },
            },
            "& .MuiInputLabel-root.Mui-focused": { color: PRIMARY },
          }}
        >
          <MenuItem value="">
            <em>Select a state</em>
          </MenuItem>
          {US_STATES.map((s) => (
            <MenuItem key={s.code} value={s.code}>
              {s.name}
            </MenuItem>
          ))}
        </TextField>
      )}

      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={loading}
        sx={{
          mt: 2,
          textTransform: "none",
          fontWeight: 700,
          fontSize: "1rem",
          py: 1.5,
          borderRadius: 2,
          backgroundColor: PRIMARY,
          color: "#ffffff",
          boxShadow: "none",
          minHeight: 48,
          "&:hover": { backgroundColor: PRIMARY_HOVER, boxShadow: "none" },
          "&.Mui-disabled": { backgroundColor: "#94a3b8", color: "#ffffff" },
        }}
        startIcon={
          loading ? (
            <CircularProgress size={18} sx={{ color: "#ffffff" }} />
          ) : (
            <HowToVoteIcon />
          )
        }
      >
        {loading ? "Loading…" : "See my elections"}
      </Button>

    </Box>
  );
}
